import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { shareReplay } from 'rxjs';
import { endpoints } from '../../utils/endpoints';
import {
    JoinAsTeamTournamentRequest,
    JoinTeamTournamentAsSinglePlayerRequest,
    JoinTournamentRequest,
    RemoveTeamTournamentMemberRequest,
} from '../models/api/requests/tournamentParticipants.request';
import { TournamentResult } from '../models/api/responses/tournamentGeneral.response';
import {
    ClosestTournamentGroup,
    ClosestTournamentMatch,
    DeleteParticipantResponse,
    JoinAsTeamTournamentResponse,
    JoinTeamTournamentAsSinglePlayerResponse,
    JoinTournamentGoodResponse,
    RemoveTeamTournamentMemberResponse,
    SendInvitationToJoinTeamTournamentResponse,
    TeamTournamentResponse,
    TournamentParticipantsResponse,
    TournamentPendingParticipantsResponse,
} from '../models/api/responses/tournamentParticipants.response';
import {
    TeamTournament,
    TournamentParticipant,
} from '../models/tournaments.interface';

@Injectable({
    providedIn: 'root',
})
export class TournamentParticipantService {
    private http = inject(HttpClient);


    fetchUserParticipation(tournamentId: string, profileId: string, useV2Base?: boolean) {
        return this.http.get<any>(endpoints.tournaments.children.checkUserParticipation(
            tournamentId,
            profileId,
            useV2Base
        ));
    }


    fetchTournamentParticipants(tournamentId: string, page:string|number, pageSize: string|number, IsAdminOrTournamentOwner: boolean) {
        return this.http
            .get<TournamentParticipantsResponse>(
                endpoints.tournaments.children.getTournamentParticipationUrl(
                    tournamentId,
                    page,
                    pageSize,
                    IsAdminOrTournamentOwner
                )
            )
            .pipe(shareReplay());
    }

    fetchTournamentPendingParticipants(tournamentId: string, page:string|number, pageSize: string|number, IsAdminOrTournamentOwner: boolean) {
        return this.http
            .get<TournamentPendingParticipantsResponse>(
                endpoints.tournaments.children.getTournamentPendingParticipationUrl(
                    tournamentId,
                    page,
                    pageSize,
                    IsAdminOrTournamentOwner
                )
            )
            .pipe(shareReplay());
    }

    deleteTournamentParticipant({ player }: { player: TournamentParticipant }) {
        return this.http.delete<DeleteParticipantResponse>(
            endpoints.tournaments.children.getDeleteTournamentParticipationUrl(
                player.tournamentId,
                player.id
            )
        );
    }

    joinToATournament(connectionData: JoinTournamentRequest) {
        return this.http.post<JoinTournamentGoodResponse>(
            endpoints.tournaments.children.getJoinFreeTournamentUrl(
                connectionData.tournamentId as string
            ),
            connectionData
        );
    }

    joinAsTeamToATournament(
        tournamentId: string,
        connectionData: JoinAsTeamTournamentRequest
    ) {
        return this.http.post<JoinAsTeamTournamentResponse>(
            endpoints.tournaments.children.getJoinAsTeamTournamentUrl(
                tournamentId
            ),
            connectionData
        );
    }

    getTeamTournamentsByPlayer(tournamentId: string, playerId: string) {
        return this.http
            .get<TeamTournamentResponse[]>(
                endpoints.tournaments.children.getTeamTournamentsByPlayerUrl(
                    tournamentId,
                    playerId
                )
            )
            .pipe(shareReplay());
    }

    getTeamTournamentInfo(tournamentId: string, participantId: string) {
        return this.http
            .get<TeamTournament>(
                endpoints.tournaments.children.getTeamTournamentInfoUrl(
                    tournamentId,
                    participantId
                )
            )
            .pipe(shareReplay());
    }

    fetchClosestMatchTournament(tournamentId: string) {
        return this.http
            .get<ClosestTournamentMatch>(
                endpoints.matches.children.getClosestTournamentMatch(
                    tournamentId
                )
            )
            .pipe(shareReplay());
    }

    fetchClosestTournamentMatchV2(tournamentId: string) {
        return this.http
            .get<ClosestTournamentGroup>(
                endpoints.matches.children.getClosestTournamentMatchV2(
                    tournamentId
                )
            )
            .pipe(shareReplay());
    }

    fetchTournamentParticipantResult(tournamentId: string) {
        return this.http
            .get<TournamentResult>(
                endpoints.matches.children.getTournamentParticipantResult(
                    tournamentId
                )
            )
            .pipe(shareReplay());
    }

    addCapitanToTeamTournament(
        tournamentId: string,
        participantId: string,
        captainId: string,
        gameAccountId: string,
        position?: string,
    ) {
        return this.http.post<SendInvitationToJoinTeamTournamentResponse>(
            endpoints.tournaments.children.getInvitePlayerToTeamTournamentUrl(
                tournamentId,
                participantId,
                captainId
            ),
            {
                gameAccountId,
                position
            }
        );
    }

    fetchAvailableTeamToJoin(tournamentId: string) {
        return this.http
            .get<TeamTournamentResponse>(
                endpoints.tournaments.children.getAvailableTeamToJoinAsSinglePlayerUrl(
                    tournamentId
                )
            )
            .pipe(shareReplay());
    }

    joinTeamTournamentAsSinglePlayer(
        tournamentId: string,
        participantId: string,
        request: JoinTeamTournamentAsSinglePlayerRequest
    ) {
        return this.http.post<JoinTeamTournamentAsSinglePlayerResponse>(
            endpoints.tournaments.children.getJoinTeamTournamentAsSinglePlayerUrl(
                tournamentId,
                participantId
            ),
            request
        );
    }

    removeTeamTournamentMember(request: RemoveTeamTournamentMemberRequest) {
        return this.http.delete<RemoveTeamTournamentMemberResponse>(
            endpoints.tournaments.children.removeTeamTournamentMember(
                request.tournamentId,
                request.teamTournamentId,
                request.memberId
            )
        );
    }
}
